<template>
    <div :class="['ai-chat-input bg-white py-3', borderless ? '' : 'border-t border-gray-200']">
        <div
            v-if="images.length"
            class="flex flex-wrap gap-2 px-4 pb-2"
        >
            <div
                v-for="(image, index) in images"
                :key="index"
                class="relative group"
            >
                <img
                    :src="image.preview"
                    class="h-16 w-16 object-cover rounded-lg border border-gray-300"
                />
                <button
                    class="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                    @click="removeImage(index)"
                >
                    <i class="far fa-times"></i>
                </button>
            </div>
        </div>
        <div class="flex items-end relative px-4">
            <div class="flex-1 relative">
                <textarea
                    ref="input"
                    v-model="message"
                    class="w-full border border-gray-400 rounded-lg px-3 py-2 text-lg resize-none focus:outline-none focus:border-blue-500 overflow-hidden relative z-10 bg-white"
                    rows="1"
                    :placeholder="suggestion && !message ? suggestion : 'Type a message...'"
                    :disabled="isStreaming"
                    @keydown.enter.exact="handleEnter"
                    @keydown.tab.exact="acceptSuggestion"
                    @input="autoResize"
                    @paste="onPaste"
                ></textarea>
            </div>
            <button
                v-if="isStreaming"
                class="text-red-500 hover:text-red-600 flex-shrink-0 p-2 -ml-1"
                @click="$emit('abort')"
            >
                <i class="far fa-stop-circle fa-fw text-lg"></i>
            </button>
            <button
                v-else
                class="text-blue-500 hover:text-blue-600 flex-shrink-0 p-2 -ml-1"
                :class="{ 'opacity-50 cursor-not-allowed': !canSend }"
                :disabled="!canSend"
                @click="send"
            >
                <i class="far fa-paper-plane fa-fw text-lg"></i>
            </button>
        </div>
    </div>
</template>

<script>
const MAX_IMAGES = 5;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

export default {
    props: {
        isStreaming: {
            type: Boolean,
            default: false,
        },
        borderless: {
            type: Boolean,
            default: false,
        },
        suggestion: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            message: '',
            images: [],
        };
    },

    computed: {
        canSend() {
            return this.message.trim() || this.images.length;
        },
    },

    watch: {
        message() {
            this.$nextTick(() => this.autoResize());
        },
        suggestion() {
            this.$nextTick(() => this.autoResize());
        },
    },

    methods: {
        autoResize() {
            const textarea = this.$refs.input;

            if (!textarea) return;

            textarea.style.height = 'auto';
            const maxHeight = 150;

            // When empty with a suggestion placeholder, measure placeholder height
            let targetHeight = textarea.scrollHeight;
            if (!this.message && this.suggestion) {
                const temp = textarea.value;
                textarea.value = this.suggestion;
                targetHeight = Math.max(targetHeight, textarea.scrollHeight);
                textarea.value = temp;
            }

            textarea.style.height = `${Math.min(targetHeight, maxHeight)}px`;
            textarea.style.overflowY = targetHeight > maxHeight ? 'auto' : 'hidden';
        },

        handleEnter(e) {
            if (!e.shiftKey) {
                e.preventDefault();
                this.send();
            }
        },

        send() {
            const text = this.message.trim();

            if ((!text && !this.images.length) || this.isStreaming) return;

            this.$emit('clearSuggestion');
            this.$emit('send', {
                text,
                images: this.images.map((img) => ({
                    data: img.base64,
                    media_type: img.media_type,
                    preview: img.preview,
                })),
            });
            this.message = '';
            this.images = [];
            if (this.$refs.input) {
                this.$refs.input.style.height = 'auto';
            }
        },

        onPaste(e) {
            const items = e.clipboardData?.items;

            if (!items) return;

            const imageFiles = [];
            Array.from(items).forEach((item) => {
                if (item.type.startsWith('image/')) {
                    imageFiles.push(item.getAsFile());
                }
            });

            if (imageFiles.length) {
                e.preventDefault();
                this.addFiles(imageFiles);
            }
        },

        addFiles(files) {
            Array.from(files).forEach((file) => {
                if (this.images.length >= MAX_IMAGES) return;

                if (!file.type.startsWith('image/')) return;

                if (file.size > MAX_IMAGE_SIZE) return;

                this.readFileAsBase64(file);
            });
        },

        readFileAsBase64(file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const dataUrl = e.target.result;
                const base64 = dataUrl.split(',')[1];
                this.images.push({
                    preview: dataUrl,
                    base64,
                    media_type: file.type,
                    name: file.name,
                });
            };
            reader.readAsDataURL(file);
        },

        removeImage(index) {
            this.images.splice(index, 1);
        },

        acceptSuggestion(e) {
            if (this.suggestion && !this.message) {
                e.preventDefault();
                this.message = this.suggestion;
                // Clear suggestion immediately when accepting
                this.$emit('clearSuggestion');
                this.$nextTick(() => {
                    this.autoResize();
                    if (this.$refs.input) {
                        this.$refs.input.focus();
                    }
                });
            }
        },

        focus() {
            if (this.$refs.input) {
                this.$refs.input.focus();
            }
        },
    },
};
</script>
